import { Clock, AlertCircle, ChevronDown } from 'lucide-react'
import type { SyncHistory, SyncStatus } from '../types'
import { SYNC_STATUS_CONFIG } from '../types'

type Props = {
  history: SyncHistory[]
  loading: boolean
  hasMore: boolean
  total: number
  onLoadMore: () => void
  onViewDetail?: (history: SyncHistory) => void
  showScheduleName?: boolean
}

function formatDuration(seconds: number | null): string {
  if (!seconds) return '-'
  if (seconds < 60) return `${seconds}s`
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}m ${secs}s`
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function StatusBadge({ status }: { status: SyncStatus }) {
  const config = SYNC_STATUS_CONFIG[status]
  return (
    <span className={`
      inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium
      ${status === 'success' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : ''}
      ${status === 'running' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' : ''}
      ${status === 'pending' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' : ''}
      ${status === 'partial' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' : ''}
      ${status === 'failed' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : ''}
      ${status === 'cancelled' ? 'bg-zinc-100 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-300' : ''}
    `}>
      <span>{config.icon}</span>
      <span>{config.label}</span>
    </span>
  )
}

function TriggerBadge({ type }: { type: 'scheduled' | 'manual' }) {
  return (
    <span className={`
      inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs
      ${type === 'scheduled' 
        ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' 
        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'}
    `}>
      {type === 'scheduled' ? '🕐 Auto' : '👆 Manuel'}
    </span>
  )
}

function MetricsBar({ history }: { history: SyncHistory }) {
  const total = history.items_new + history.items_updated + history.items_skipped + history.items_failed
  if (total === 0) return <span className="text-zinc-400">-</span>
  
  return (
    <div className="flex items-center gap-2 text-xs">
      {history.items_new > 0 && (
        <span className="text-emerald-600 dark:text-emerald-400">+{history.items_new}</span>
      )}
      {history.items_updated > 0 && (
        <span className="text-blue-600 dark:text-blue-400">↻{history.items_updated}</span>
      )}
      {history.items_skipped > 0 && (
        <span className="text-zinc-500">≡{history.items_skipped}</span>
      )}
      {history.items_failed > 0 && (
        <span className="text-red-600 dark:text-red-400">✗{history.items_failed}</span>
      )}
    </div>
  )
}

export function SyncHistoryTable({
  history,
  loading,
  hasMore,
  total,
  onLoadMore,
  onViewDetail,
  showScheduleName = true,
}: Props) {
  if (loading && history.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  if (history.length === 0) {
    return (
      <div className="text-center py-12 text-zinc-500 dark:text-zinc-400">
        <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p className="text-lg font-medium">Aucun historique</p>
        <p className="text-sm mt-1">Les synchronisations apparaîtront ici</p>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-200 dark:border-zinc-700 text-left">
            <th className="pb-3 font-medium text-zinc-600 dark:text-zinc-400">Date</th>
            {showScheduleName && (
              <th className="pb-3 font-medium text-zinc-600 dark:text-zinc-400">Configuration</th>
            )}
            <th className="pb-3 font-medium text-zinc-600 dark:text-zinc-400">Statut</th>
            <th className="pb-3 font-medium text-zinc-600 dark:text-zinc-400">Type</th>
            <th className="pb-3 font-medium text-zinc-600 dark:text-zinc-400">Résultats</th>
            <th className="pb-3 font-medium text-zinc-600 dark:text-zinc-400">Durée</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
          {history.map(item => (
            <tr 
              key={item.public_id}
              onClick={() => onViewDetail?.(item)}
              className={`
                ${onViewDetail ? 'cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800/50' : ''}
              `}
            >
              <td className="py-3 text-zinc-900 dark:text-zinc-100">
                {formatDate(item.started_at)}
              </td>
              {showScheduleName && (
                <td className="py-3 text-zinc-700 dark:text-zinc-300">
                  {item.schedule_name}
                </td>
              )}
              <td className="py-3">
                <StatusBadge status={item.status} />
              </td>
              <td className="py-3">
                <TriggerBadge type={item.trigger_type} />
              </td>
              <td className="py-3">
                <MetricsBar history={item} />
              </td>
              <td className="py-3 text-zinc-600 dark:text-zinc-400">
                {formatDuration(item.duration_seconds)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      
      {/* Error display for failed items */}
      {history.some(h => h.status === 'failed' && h.error_message) && (
        <div className="mt-4 space-y-2">
          {history.filter(h => h.status === 'failed' && h.error_message).map(h => (
            <div 
              key={h.public_id}
              className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-sm"
            >
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-medium text-red-700 dark:text-red-300">
                  {h.schedule_name}:
                </span>
                <span className="ml-1 text-red-600 dark:text-red-400">
                  {h.error_message}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Load more */}
      {hasMore && (
        <div className="mt-4 text-center">
          <button
            onClick={onLoadMore}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg
              text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20
              disabled:opacity-50"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
            Charger plus ({history.length}/{total})
          </button>
        </div>
      )}
    </div>
  )
}

export default SyncHistoryTable
